/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import spwsUnlockInfo from './spwsUnlockInfo';
import api from '../../../../../../share/src/util/api/service';
import { POLLING_ERROR_GET_INVALID_RESPONSE } from '../../../../../../share/src/util/api/polling';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import { executeSyncRequest } from '../../../../../../share/src/util/api/syncRequest';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import { getAbbrSysName } from '../../../../share/util/systemName';
import loading from '../../../../share/components/vue/ProcessingIcon';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction.js';
const DiagSystemUtils = require('../../../../common/nodejs/diagSystemUtils');

/** 各フェーズ定義 */
const PHASE = {
  INPUT_PASSWORD: 'inputPassword',
  INPUT_ERROR: 'inputError'
};

// COM1用設定
const FUNC_COM1 = {
  special_key: 0,
  immutable_id: [999],
  special_requirement_id: ["COM1"]
};


// パスワードが必要か判定するためのリスト
const SPWS_KEY_LIST = {
  CTRL_ALT_J: 0,
  CTRL_ALT_D: 3,
};

// 取得したメニューリスト
let menuList = [];
// メニュー取得を行うidリスト
let getMenuIdList = [];

export default {
  name: 'hiddenFunction',
  data() {
    return {
      phase: PHASE.INPUT_PASSWORD,
      processing: false,
      isLoading: false,
      systemId: '',
      // ダイアログフラグ
      modal_hiddenFunction: false,
      validateParam: undefined,
      inputPass: '',
      targetFuncInfo: '',
      targetSystem: '',
      immutableId: [],
      specialRequirementId: '',
      isRequiredPass: true
    };
  },

  props: {
    targetSpKey: { type: Number, default: '', required: true },
    immutableIdList: { type: Array, default: '', required: true }
  },

  components: {
    'processing-view': loading
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.INPUT_ERROR:
          return 'danger';
        case PHASE.INPUT_PASSWORD:
        default:
          return 'secondary';
      }
    },

    /**
     * ダイアログのヘッダータイトル
     */
    headerTitle() {
      // パスワード入力画面
      const ConfirmationTitle = 'message.common_title_confirmation';
      // パスワード入力エラー画面
      const ErrorTitle = 'message.common_title_error';
      const titleMap = {
        inputPassword: ConfirmationTitle,
        inputError: ErrorTitle
      };
      return this.$t(titleMap[this.phase]);
    },

    /**
     * 入力欄/キャンセルボタンを表示するかどうか
     * ※どちらもパスワード入力画面でのみ表示
     */
    showInputFormAndCancelButton() {
      const showInputAndButtonMap = {
        inputPassword: true
      };
      let result = showInputAndButtonMap[this.phase];
      if (typeof result === 'undefined') {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期処理 */
    init() {
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      getMenuIdList = this.immutableIdList;

      this.getInformation();

      // 入力キーに対するECUと現在のECUとを比較する
      if (!this.checkSystem()) {
        // 対象ECUでなければ処理終了
        this.closeWizard();
        return;
      }

      if (!this.isRequiredPass) {
        // パスワード不要機能
        this.noPasswordFunction();
      } else {
        // パスワードが必要な機能はモーダルを表示する
        this.modal_hiddenFunction = true;
      }
    },

    /**
     * 処理を実行するECUか確認する
     * @returns 処理実行するかどうか (true: 処理実行)
     */
    checkSystem() {
      switch (this.targetSpKey) {
        case FUNC_COM1.special_key:
          // 'COM1': 現在ECUがABGの場合は実行しない
          if (DiagSystemUtils.isAirbagByAbbreviationId(getAbbrSysName(this.systemId))) {
            return false;
          }
          return true;
        default:
          break;
      }

      if (!this.targetSystem) {
        return false;
      }
      return true;
    },

    /**
     * 現在選択中ECUの診断システムID略称を返却する
     * @returns {string | null} 診断システムID略称
     */
    getCurrentSystemAbbreviationId() {
      return getAbbrSysName(this.systemId);
    },

    /**
     * 対象機能の情報を取得するゲッター
     */
    getInformation() {

      switch (this.targetSpKey) {
        case SPWS_KEY_LIST.CTRL_ALT_J:
          // 'COM1'の場合は「.\spwsUnlockInfo.js」から情報を取得しない
          this.targetFuncInfo = FUNC_COM1;
          this.specialRequirementId = FUNC_COM1.special_requirement_id;
          this.immutableId = FUNC_COM1.immutable_id;
          // パスワード不要
          this.isRequiredPass = false;
          return;
        case SPWS_KEY_LIST.CTRL_ALT_D:
          // ES1
          // パスワード不要だけ設定する
          this.isRequiredPass = false;
          break;
        default:
          break;
      }
      // 対象機能の「./spwsUnlockInfo.js」に記載された情報を取得しておく
      this.targetFuncInfo = spwsUnlockInfo.SPWS_UNLOCK_INFOS.find(obj => obj.special_key === this.targetSpKey).info;
      const currentAbbreviationId = this.getCurrentSystemAbbreviationId();
      this.targetSystem = this.targetFuncInfo.find((obj) => obj.abbreviation_id === currentAbbreviationId) || null;
      this.specialRequirementId = this.targetSystem ? this.targetSystem.special_requirement_id : null;
      this.immutableId = this.targetSystem ? this.targetSystem.immutable_id : null;
    },

    /**
     * パスワード不要機能の処理
     * メニュー取得
     */
    async noPasswordFunction() {
      // メニューリストを取得
      await this.getMenuList();
      if (menuList) {
        // メニューリストが取得できたら親コンポーネントに値を返す
        this.$emit('get-sp-menu', menuList, getMenuIdList);
      }
      // メニュー取得できない場合は処理終了
      this.closeWizard();
    },

    /**
     * パスワード必要機能の処理
     * 入力パスワードチェック ⇒ メニュー取得
     */
    async hiddenFunctionality() {

      // パスワードチェック
      if (await this.checkPassword()) {
        // メニューリストを取得
        await this.getMenuList();
        if (menuList) {
          // メニューリストが取得できたら親コンポーネントに値を返す
          this.$emit('get-sp-menu', menuList, getMenuIdList);
        }
        // メニュー取得できない場合は処理終了
        this.closeWizard();

      } else {
        // パスワード入力エラー画面に遷移
        this.phase = PHASE.INPUT_ERROR;
      }
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_hiddenFunction = false;
    },

    /**
     * 表示文言
     */
    displayMessage() {
      const messageMap = {
        inputPassword: 'individual.input_password_spws',
        inputError: 'individual.discrepancy_password_spws'
      };
      return this.$t(messageMap[this.phase]);
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /**
     * エラー時は処理終了する
     */
    showUnexpectedError() {
      this.closeWizard();
    },

    /**
     * 入力内容のバリデーションチェック
     */
    validate() {
      this.validateParam = undefined;

      // 入力したパスワードが入力規則に沿っているか確認(半角数字のみ)
      const regex = new RegExp(/^[0-9]*$/);
      if (!regex.test(this.inputPass) || this.inputPass === '') {
        // 入力規則に沿っていない場合はアラートを表示する
        this.validateParam = { valid: true, msg: this.$t(`message.alert_validate_invalid_pattern`) };
        return false;
      } else {
        return true;
      }
    },

    // API実行 ----------------------------------
    /**
     * 対象機能を含むメニューサポートリストを取得する
     */
    async getMenuList() {
      try {
        // パスワード不要機能はモーダル表示しないためprocessingを使用
        this.processing = true;
        if (this.immutableId.length > 0) {
          // メニュー取得を行いたい不変IDリスト
          getMenuIdList = getMenuIdList.concat(this.immutableId);
        }

        const response = await executeSyncRequest(
          this,
          // GET API 呼び出しのコールバック
          async () => {
            return await api.getParamCall(
              api.WSP,
              '/netApps/' + this.netAppId + '/systems/' + this.systemId + '/menus',
              {
                immutable_id: JSON.stringify(getMenuIdList)
              }
            );
          },
          // エラー発生のコールバック 
          (errorType) => { errorType; this.closeWizard(); },
          // GET API レスポンスチェックのコールバック
          (getApiResponse) => {
            // menus が入っていれば成功とする
            return getApiResponse?.data?.menus ? '' : POLLING_ERROR_GET_INVALID_RESPONSE;
          },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );
        // 処理失敗時は response が null になるので考慮
        menuList = response ? response.data.menus : null;
        return;
      } finally {
        this.processing = false;
      }
    },

    /**
     * パスワードチェックを行う
     * @returns パスワードチェック結果
     */
    async checkPassword() {
      try {
        this.isLoading = true;
        let pinVerifiedRes = null;
        const resultList = [];

        for (const id of this.specialRequirementId) {
          pinVerifiedRes = await individualWorkSupportUtil.postVerifyPinNumber(
            this,
            id,
            this.systemId,
            {
              pin_number: this.inputPass
            }
          );

          resultList.push(pinVerifiedRes?.data?.verified ? pinVerifiedRes.data.verified : false);
        }

        // チェック結果を返す 1機能でもパスワードチェックで失敗していたら失敗を返す
        return resultList.some(res => res === false) ? false : true;
      } finally {
        this.isLoading = false;
      }
    },

    // ユーザー操作 ----------------------------------
    
    /**
     * OK押下時の処理
     */
    onOkClicked() {
      switch (this.phase) {
        case PHASE.INPUT_PASSWORD:
          this.inputOkClicked();
          break;
        case PHASE.INPUT_ERROR:
          this.closeButton();
          break;
        default:
          break;
      }
    },

    /**
     * パスワード入力エラー画面にてOK押下または
     * パスワード入力画面にてキャンセル押下
     */
    closeButton() {
      this.closeWizard();
    },

    /**
     * パスワード入力画面にてOK押下
     */
    inputOkClicked() {
      if (this.validate()) {
        // validate OKであれば処理を進める
        this.hiddenFunctionality();
      }
    }
  },

  async mounted() {
    this.init();
  },
};